import React from 'react';

import Layout from './Layout';
import Header from './Header';
import Footer from './Footer';

const FUNDRAISERS_HELP_URL =
  'https://help.erg.zone/article/420-fundraisers-on-ergzone-overview';
const FUNDRAISERS_COLLECTION_URL =
  'https://help.erg.zone/collection/416-fundraisers';

const sectionDarkStyle = {
  background: '#333333',
  backgroundImage: 'none',
};

const openStartFundraiserBeacon = () => {
  if (typeof window === 'undefined') return;

  if (window.plausible) {
    window.plausible('StartFundraiser Click');
  }
  const content = `I'm interested in starting a fundraiser on ErgZone.\n\n Fundraiser Name:\n Fundraiser Description:\n Goal Amount:\n Start Date:\n End Date:`;
  if (window.Beacon) {
    window.Beacon('prefill', {
      subject: 'New Fundraiser',
      text: content,
    });
    window.Beacon('navigate', '/ask/message');
    window.Beacon('open');
  }
};

const FUNDRAISE_BASE_URL = 'https://comp.erg.zone';

const StartFundraiserButton = ({ className, style }) => (
  <a
    href="#"
    className={className}
    style={style}
    onClick={(e) => {
      e.preventDefault();
      openStartFundraiserBeacon();
    }}
  >
    Start a Fundraiser
  </a>
);

const formatFundraiseDates = (startDate, endDate) => {
  const format = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const start = format(startDate);
  const end = format(endDate);
  if (start && end) return `${start} – ${end}`;
  return start || end || null;
};

const formatRaisedAmount = (cents, currency) => {
  if (cents == null) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch (_err) {
    return `$${(cents / 100).toFixed(0)}`;
  }
};

const isCurrentCampaign = (fundraiser) => {
  if (fundraiser.active === false) return false;
  if (!fundraiser.endDate) return !!fundraiser.active;
  return new Date(fundraiser.endDate) > new Date();
};

const ActiveFundraisersList = ({ fundraisers }) => {
  if (!fundraisers || fundraisers.length === 0) {
    return (
      <p className="text-center text-muted mb-0">
        There are no active fundraisers right now. Check back soon, or start one
        of your own.
      </p>
    );
  }

  return (
    <div className="row">
      {fundraisers.map((fundraiser) => {
        const href = `${FUNDRAISE_BASE_URL}/fundraise/${fundraiser.id}`;
        const organizer = fundraiser.mainSource?.name;
        const imageUrl = fundraiser.mainSource?.imageUrl;
        const dates = formatFundraiseDates(
          fundraiser.startDate,
          fundraiser.endDate
        );
        const raised = formatRaisedAmount(
          fundraiser.raisedAmountCents,
          fundraiser.currency
        );
        const status = isCurrentCampaign(fundraiser)
          ? 'Active Campaign'
          : 'Past Success';

        return (
          <div className="col-md-6 mb-4" key={fundraiser.id}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-start text-decoration-none"
              style={{ color: 'inherit' }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="mr-3"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  className="mr-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: '#e9ecef',
                    flexShrink: 0,
                    color: '#00c9be',
                  }}
                >
                  <i className="fas fa-heart" aria-hidden="true" />
                </div>
              )}
              <div>
                <p
                  className="mb-1"
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: isCurrentCampaign(fundraiser)
                      ? '#00c9be'
                      : 'rgba(255, 255, 255, 0.55)',
                  }}
                >
                  {status}
                </p>
                <h3 className="h5 mb-1">{fundraiser.name}</h3>
                {organizer && (
                  <p
                    className="mb-1 text-muted"
                    style={{ fontSize: '0.95rem' }}
                  >
                    By {organizer}
                  </p>
                )}
                {raised && (
                  <p
                    className="mb-1"
                    style={{ fontSize: '0.95rem', fontWeight: 600 }}
                  >
                    {raised} raised
                  </p>
                )}
                {dates && (
                  <p
                    className="mb-0 text-muted"
                    style={{ fontSize: '0.875rem' }}
                  >
                    {dates}
                  </p>
                )}
              </div>
            </a>
          </div>
        );
      })}
    </div>
  );
};

const FundraisersPage = ({ pageContext }) => {
  const fundraisers = pageContext?.fundraisers || [];

  return (
    <Layout titlePrefixes={['Fundraisers']}>
      <style>{`
      .section-dark .section-heading h2,
      .section-dark .section-heading p,
      .section-dark h3,
      .section-dark h4,
      .section-dark p,
      .section-dark .text-muted,
      .section-dark .lead { color: rgba(255, 255, 255, 0.95); }
      .section-dark .feature-item h3,
      .section-dark .feature-item p { color: rgba(255, 255, 255, 0.9); }
      .section-dark .feature-item i { color: #00c9be !important; }
      .section-dark hr { border-color: rgba(255, 255, 255, 0.3); }

      .feature-item i {
        background: -webkit-linear-gradient(to left, #9FF4EB 0%, #00c9be 40%, #00504c 75%, #001210 100%) !important;
        background: linear-gradient(to left, #9FF4EB 0%, #00c9be 40%, #00504c 75%, #001210 100%) !important;
        -webkit-background-clip: text !important;
        background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
      }
    `}</style>
      <Header />

      <header className="masthead text-center" id="top">
        <div className="container">
          <div className="row h-100">
            <div className="col-lg-7 my-auto">
              <div className="header-content mx-auto">
                <h1 className="mb-4">Fundraisers on ErgZone</h1>
                <p className="lead mb-4">
                  Launch initiatives that combine athlete participation,
                  real-time workout data, and seamless donations. Your workouts,
                  leaderboards, and contributors now live in one place.
                </p>
                <div className="d-flex flex-column flex-sm-row">
                  <StartFundraiserButton
                    className="btn btn-outline btn-xl mr-sm-3 mb-2 mb-sm-0"
                    style={{ whiteSpace: 'nowrap' }}
                  />
                  <a href="#featured" className="btn btn-outline btn-xl">
                    Browse
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-5 my-auto">
              <div className="device-container">
                <div
                  style={{
                    position: 'relative',
                    aspectRatio: '9/16',
                    maxWidth: '325px',
                    margin: '0 auto',
                    overflow: 'hidden',
                    borderRadius: '12px',
                  }}
                >
                  <iframe
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px',
                    }}
                    src="https://www.youtube.com/embed/ep-yApbZhCg?rel=0"
                    referrerpolicy="strict-origin-when-cross-origin"
                    title="ErgZone Fundraisers Overview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    frameBorder="0"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="features" id="different">
        <div className="container">
          <div className="section-heading text-center">
            <h2>What Makes ErgZone Fundraisers Different</h2>
            <p className="text-muted">
              A fully integrated experience with no separate systems to connect
            </p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <p className="text-center lead">
                Unlike traditional fundraising tools that only track how much
                money has been raised, ErgZone centralizes the entire campaign.
                Athletes complete workouts directly in the app, meters are
                tracked automatically, and supporters donate to specific
                participants—all in one seamless flow.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        className="features section-dark"
        id="how-it-works"
        style={sectionDarkStyle}
      >
        <div className="container">
          <div className="section-heading text-center">
            <h2>How It Works</h2>
            <p className="text-muted">
              An ErgZone fundraiser brings together these pieces
            </p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-bullseye" style={{ color: '#00c9be' }}></i>
                <h3>The Fundraiser</h3>
                <p className="text-muted">
                  Defines your purpose, financial goals, and donation period.
                  This is the &quot;home base&quot; for your campaign.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-users" style={{ color: '#00c9be' }}></i>
                <h3>The Events</h3>
                <p className="text-muted">
                  One or more ErgZone events or groups linked to the fundraiser.
                  These determine where athletes register and which meters are
                  counted.
                </p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-running" style={{ color: '#00c9be' }}></i>
                <h3>The Athletes</h3>
                <p className="text-muted">
                  Participants register for the associated event and complete
                  their workouts directly through the ErgZone app.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="feature-item">
                <i className="fas fa-heart" style={{ color: '#00c9be' }}></i>
                <h3>The Supporters</h3>
                <p className="text-muted">
                  Friends and family donate via a single link, selecting the
                  specific athlete they wish to support during the checkout
                  process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features" id="tracked">
        <div className="container">
          <div className="section-heading text-center">
            <h2>What Is Tracked</h2>
            <p className="text-muted">
              Real-time visibility for organizers, athletes, and supporters
            </p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-4">
              <div className="feature-item">
                <i className="fas fa-user text-primary"></i>
                <h3>Donations per Athlete</h3>
                <p className="text-muted">
                  Track individual impact as donations are attributed to
                  specific athletes in real time.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="feature-item">
                <i className="fas fa-chart-line text-primary"></i>
                <h3>
                  Total Funds
                  <br />
                  Raised
                </h3>
                <p className="text-muted">
                  A live, transparent view of the total contributions across the
                  entire campaign.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="feature-item">
                <i className="fas fa-tachometer-alt text-primary"></i>
                <h3>
                  Meters per
                  <br />
                  Athlete
                </h3>
                <p className="text-muted">
                  Every meter completed in associated events is automatically
                  totaled for each athlete.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="features section-dark"
        id="supporters-experience"
        style={{ background: '#000', backgroundImage: 'none' }}
      >
        <div className="container">
          <div className="section-heading text-center">
            <h2>A Seamless Experience for Supporters</h2>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <p className="text-center mb-4">
                We've built the fundraiser experience to be as smooth for your
                supporters as it is for your athletes. Watch the video below to
                see how easy it is for donors to contribute to a mission and
                support their favorite athletes in just a few clicks.
              </p>
              <div
                className="embed-responsive embed-responsive-16by9"
                style={{
                  maxWidth: '800px',
                  margin: '0 auto',
                  overflow: 'hidden',
                  borderRadius: '12px',
                }}
              >
                <iframe
                  className="embed-responsive-item"
                  style={{ borderRadius: '12px' }}
                  src="https://www.youtube.com/embed/-w_BllRVtu0?rel=0"
                  referrerpolicy="strict-origin-when-cross-origin"
                  title="ErgZone Fundraisers - Supporter Experience"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features" id="payments">
        <div className="container">
          <div className="section-heading text-center">
            <h2>Payment Processing</h2>
            <p className="text-muted">Secure donations powered by Stripe</p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <p className="text-center">
                All donations are processed through a Stripe account connected
                directly to your fundraiser. This ensures that every
                contribution is securely attributed to an athlete for real-time
                tracking and visibility. To prioritize security, ErgZone never
                stores sensitive payment information.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        className="features section-dark"
        id="featured"
        style={sectionDarkStyle}
      >
        <div className="container">
          <div className="section-heading text-center">
            <h2>Featured Campaigns</h2>
            <p className="text-muted">
              Discover how athletes and organizations are making an impact
              through ErgZone.
            </p>
            <hr />
          </div>
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <ActiveFundraisersList fundraisers={fundraisers} />
            </div>
          </div>
        </div>
      </section>

      <section className="features" id="faq">
        <div className="container">
          <div className="section-heading text-center">
            <h2>Frequently Asked Questions</h2>
            <hr />
          </div>

          <div className="row">
            <div className="col-lg-10 mx-auto">
              <h3 className="mb-4 mt-5">For Organizers</h3>

              <div className="mb-4">
                <h4 className="mb-2">
                  What do I need to start a fundraiser on ErgZone?
                </h4>
                <p className="text-muted">
                  To get started, you'll need an ErgZone account and a clear
                  goal for your campaign. Since all donations are processed
                  securely, you will also need to connect a Stripe account. Our
                  team will guide you through this process during setup.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">How do I set up my payment processing?</h4>
                <p className="text-muted">
                  ErgZone uses Stripe Connect to ensure funds are routed
                  directly to you. Once you begin your setup, our team will send
                  you a specific link to connect your account to ErgZone's
                  Stripe Connect platform. This setup ensures that ErgZone never
                  stores sensitive financial data and that you have full control
                  over your payouts.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">Are there fees for using the platform?</h4>
                <p className="text-muted">
                  ErgZone charges a 3% platform fee for fundraisers, and
                  standard Stripe processing fees also apply. These fees are
                  deducted automatically when a donation is made, so there are
                  no invoices for you to manage. Typically, the donor has the
                  option—and often chooses—to cover these fees during the
                  donation process, ensuring the full donation amount goes
                  directly to your fundraiser.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">
                  Can I link multiple events or groups to one fundraiser?
                </h4>
                <p className="text-muted">
                  Yes. You can associate one or more ErgZone events or groups
                  with a single fundraiser. This allows you to track meters
                  across different challenges—such as a specific competition and
                  a training group—all toward one fundraising goal.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">How are meters tracked?</h4>
                <p className="text-muted">
                  Meters are automatically pulled from workouts completed by
                  registered athletes within your associated ErgZone events or
                  groups. This eliminates the need for manual reporting;
                  however, if your specific event or group settings allow for
                  manual entry, those meters will also count toward the
                  fundraiser total.
                </p>
              </div>

              <h3 className="mb-4 mt-5">For Athletes</h3>

              <div className="mb-4">
                <h4 className="mb-2">How do I participate in a fundraiser?</h4>
                <p className="text-muted">
                  Athletes participate by registering for the specific ErgZone
                  event or group linked to the fundraiser. Once you are
                  registered and the fundraiser is active, the workouts you
                  complete in that event will contribute meters toward the goal.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">
                  Do I need to report my meters manually?
                </h4>
                <p className="text-muted">
                  In most cases, no. As long as you complete your workouts
                  through the ErgZone app within the linked event, your meters
                  are updated on the fundraiser leaderboard in real time. If the
                  organizer has enabled manual reporting for the event, you can
                  also log meters that way.
                </p>
              </div>

              <h3 className="mb-4 mt-5">For Supporters</h3>

              <div className="mb-4">
                <h4 className="mb-2">Can I donate to a specific athlete?</h4>
                <p className="text-muted">
                  Yes. When you visit the fundraiser page, you will have the
                  option to select a specific athlete to support. Your donation
                  will be attributed to them for tracking and visibility on the
                  live leaderboard.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">Can I donate anonymously?</h4>
                <p className="text-muted">
                  Yes. You can choose to make your donation anonymously when
                  completing the checkout process. Your contribution will still
                  count toward the athlete and fundraiser totals, but your name
                  will not be displayed publicly.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">Is my payment information secure?</h4>
                <p className="text-muted">
                  Absolutely. All donations are processed through Stripe, a
                  global leader in payment security. Because we use Stripe
                  Connect, your sensitive payment details never touch ErgZone's
                  servers.
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-2">What payment methods can I use?</h4>
                <p className="text-muted">
                  Supporters can donate using any of the payment methods enabled
                  for the fundraiser, including major credit cards and digital
                  wallets like Apple Pay or Google Pay. You do not need an
                  ErgZone account to make a donation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="bg-primary text-center"
        id="cta"
        style={{ paddingTop: '60px', paddingBottom: '80px' }}
      >
        <div className="container">
          <div className="row">
            <div className="col-md-8 mx-auto">
              <h2 className="section-heading">
                Ready to Launch Your Campaign?
              </h2>
              <p
                className="text-muted"
                style={{ marginTop: '12px', marginBottom: '32px' }}
              >
                Tell us about your fundraiser and we&apos;ll help you get set
                up.
              </p>
              <div style={{ marginBottom: '40px' }}>
                <StartFundraiserButton className="btn btn-light btn-xl" />
              </div>

              <p style={{ marginBottom: '16px' }}>
                Prefer to read up first? Check out our overview or browse the
                full help library for setup guides and FAQs.
              </p>
              <div style={{ marginBottom: '28px' }}>
                <a
                  href={FUNDRAISERS_HELP_URL}
                  className="btn btn-outline btn-xl mr-3 mb-2 mb-md-0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Fundraisers overview
                </a>
                <a
                  href={FUNDRAISERS_COLLECTION_URL}
                  className="btn btn-outline btn-xl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  All help articles
                </a>
              </div>

              <p style={{ marginTop: '8px', paddingBottom: '24px' }}>
                Have questions?{' '}
                <a
                  href="#"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.preventDefault();
                    window.Beacon('prefill', {
                      subject: 'Fundraiser Help',
                      text: '',
                    });
                    window.Beacon('navigate', '/ask/message');
                    window.Beacon('open');
                  }}
                >
                  Contact us
                </a>{' '}
                and we&apos;ll help you plan your event.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </Layout>
  );
};

export default FundraisersPage;
